import {
  CoreUserGroups,
  Organisation,
  Scopes,
  coreUserGroupScopes as getCoreUserGroupScopes,
  userGroupScopes as getUserGroupScopes
} from '@simplyanvil/cl-types';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import type { UserOrganisation } from '@/contexts/OrganisationContext';

export interface ResolveEffectiveScopesInput {
  currentOrganisation?: Pick<Organisation, 'id' | 'parentId'>;
  organisations: UserOrganisation[];
  user?: { access?: { groups?: Array<CoreUserGroups | string>; scopes?: Array<Scopes | string> } };
}

const expandOrgScopes = (org?: UserOrganisation): Scopes[] => {
  if (!org) return [];
  const groupScopes = flatten((org.access?.groups || []).map((group) => getUserGroupScopes[group])) as Scopes[];
  return [...((org.access?.scopes || []) as Scopes[]), ...groupScopes];
};

export const resolveEffectiveScopes = ({
  currentOrganisation,
  organisations,
  user
}: ResolveEffectiveScopesInput): Scopes[] => {
  const userGroupScopes = flatten(
    (user?.access?.groups || []).map((group) => getCoreUserGroupScopes[group as CoreUserGroups])
  ) as Scopes[];
  const userScopes: Scopes[] = [...userGroupScopes, ...((user?.access?.scopes || []) as Scopes[])];

  const directOrg = find(organisations, { orgId: currentOrganisation?.id });
  const directScopes = expandOrgScopes(directOrg);

  // Single-hop parent inheritance only — Enterprise Plus's parent → sub-org
  // model has no grandparent today.
  const parentId = currentOrganisation?.parentId;
  const parentOrg = parentId ? find(organisations, { orgId: parentId }) : undefined;
  const parentScopes = expandOrgScopes(parentOrg);

  return uniq([...userScopes, ...directScopes, ...parentScopes]);
};

// Master-scope short-circuit: a user holding USR_EEZI_ADMIN is treated as
// holding every scope, mirroring the backend authorizer's admin bypass. Used
// by OrganisationContext.hasAccess so platform admins can act on any org —
// including ones they are not direct members of.
export const grantsAccess = (scopes: Scopes[], requested: Scopes): boolean =>
  scopes.includes(Scopes.USR_EEZI_ADMIN) || scopes.includes(requested);
